import React, { useMemo } from 'react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { ChartContainer, ChartTooltip } from '../ui/charts';
import { CHART_COLORS, SERIES_PALETTE, CHART_CONFIG, AXIS_STYLE, GRID_STYLE } from '../ui/charts/chartTheme';
import { formatCurrency } from '../../utils/formatCurrency';
import { monthKeysBetween, monthKeyOf, monthLabel } from '../../utils/reportRange';

/**
 * ReportChart — Renders the appropriate chart type based on report config.
 * Supports: area, line, bar, donut, funnel.
 */
export default function ReportChart({ chartType, chartConfig = {}, data = [], range, loading = false, onDataPointClick }) {
  // Aggregate data for chart (group by the configured dimension)
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return aggregateChartData(data, chartConfig, range);
  }, [data, chartConfig, range]);

  const isEmpty = !loading && chartData.length === 0;
  const height = chartConfig.height || 280;

  if (chartType === 'area') {
    return (
      <ChartContainer height={height} loading={loading} empty={isEmpty} chartType="area">
        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
          <defs>
            <linearGradient id="reportAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.2} />
              <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey={chartConfig.xKey || 'label'} {...AXIS_STYLE} />
          <YAxis {...AXIS_STYLE} tickFormatter={getFormatter(chartConfig.yFormat)} width={55} />
          <Tooltip content={<ChartTooltip formatter={getTooltipFormatter(chartConfig.yFormat)} />} />
          <Area
            type="monotone"
            dataKey={chartConfig.yKey || 'value'}
            name={chartConfig.yLabel || 'Value'}
            stroke={CHART_COLORS.primary}
            strokeWidth={CHART_CONFIG.strokeWidth}
            fill="url(#reportAreaGradient)"
            dot={{ r: CHART_CONFIG.dotRadius, fill: CHART_COLORS.primary }}
            activeDot={{
              r: CHART_CONFIG.activeDotRadius,
              cursor: onDataPointClick ? 'pointer' : 'default',
              onClick: onDataPointClick ? (e, payload) => onDataPointClick(payload?.payload) : undefined,
            }}
            animationDuration={CHART_CONFIG.animationDuration}
          />
          {chartConfig.secondaryYKey && (
            <Area
              type="monotone"
              dataKey={chartConfig.secondaryYKey}
              name={chartConfig.secondaryYLabel || 'Secondary'}
              stroke={CHART_COLORS.secondary}
              strokeWidth={CHART_CONFIG.strokeWidth}
              fill="none"
              dot={{ r: 3, fill: CHART_COLORS.secondary }}
              animationDuration={CHART_CONFIG.animationDuration}
            />
          )}
        </AreaChart>
      </ChartContainer>
    );
  }

  if (chartType === 'line') {
    return (
      <ChartContainer height={height} loading={loading} empty={isEmpty} chartType="area">
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey={chartConfig.xKey || 'label'} {...AXIS_STYLE} />
          <YAxis {...AXIS_STYLE} tickFormatter={getFormatter(chartConfig.yFormat)} width={55} />
          <Tooltip content={<ChartTooltip formatter={getTooltipFormatter(chartConfig.yFormat)} />} />
          <Line
            type="monotone"
            dataKey={chartConfig.yKey || 'value'}
            name={chartConfig.yLabel || 'Value'}
            stroke={CHART_COLORS.primary}
            strokeWidth={CHART_CONFIG.strokeWidth}
            dot={{ r: CHART_CONFIG.dotRadius, fill: CHART_COLORS.primary }}
            activeDot={{
              r: CHART_CONFIG.activeDotRadius,
              cursor: onDataPointClick ? 'pointer' : 'default',
              onClick: onDataPointClick ? (e, payload) => onDataPointClick(payload?.payload) : undefined,
            }}
            animationDuration={CHART_CONFIG.animationDuration}
          />
          {chartConfig.secondaryYKey && (
            <Line
              type="monotone"
              dataKey={chartConfig.secondaryYKey}
              name={chartConfig.secondaryYLabel || 'Secondary'}
              stroke={CHART_COLORS.secondary}
              strokeWidth={CHART_CONFIG.strokeWidth}
              dot={{ r: 3, fill: CHART_COLORS.secondary }}
              animationDuration={CHART_CONFIG.animationDuration}
            />
          )}
          <Legend />
        </LineChart>
      </ChartContainer>
    );
  }

  if (chartType === 'bar') {
    const series = chartConfig.series || [{ key: chartConfig.yKey || 'value', label: chartConfig.yLabel || 'Value' }];
    return (
      <ChartContainer height={height} loading={loading} empty={isEmpty} chartType="area">
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey={chartConfig.xKey || 'label'} {...AXIS_STYLE} />
          <YAxis {...AXIS_STYLE} tickFormatter={getFormatter(chartConfig.yFormat)} width={55} />
          <Tooltip content={<ChartTooltip formatter={getTooltipFormatter(chartConfig.yFormat)} />} />
          {series.map((s, idx) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={SERIES_PALETTE[idx % SERIES_PALETTE.length]}
              radius={[4, 4, 0, 0]}
              animationDuration={CHART_CONFIG.animationDuration}
              cursor={onDataPointClick ? 'pointer' : 'default'}
              onClick={onDataPointClick ? (barData) => onDataPointClick(barData) : undefined}
            />
          ))}
          {series.length > 1 && <Legend />}
        </BarChart>
      </ChartContainer>
    );
  }

  if (chartType === 'donut') {
    const segments = chartData.map((d, idx) => ({
      ...d,
      fill: SERIES_PALETTE[idx % SERIES_PALETTE.length],
    }));
    return (
      <ChartContainer height={height} loading={loading} empty={isEmpty} chartType="donut">
        <PieChart>
          <Pie
            data={segments}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={2}
            dataKey={chartConfig.yKey || 'value'}
            nameKey={chartConfig.xKey || 'label'}
            animationDuration={CHART_CONFIG.animationDuration}
          >
            {segments.map((entry, idx) => (
              <Cell key={idx} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip formatter={getTooltipFormatter(chartConfig.yFormat)} />} />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      </ChartContainer>
    );
  }

  if (chartType === 'funnel') {
    // Render funnel as horizontal stacked bars
    return (
      <ChartContainer height={height} loading={loading} empty={isEmpty} chartType="area">
        <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 0 }}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis type="number" {...AXIS_STYLE} />
          <YAxis type="category" dataKey={chartConfig.xKey || 'label'} {...AXIS_STYLE} width={80} />
          <Tooltip content={<ChartTooltip />} />
          <Bar
            dataKey={chartConfig.yKey || 'value'}
            name={chartConfig.yLabel || 'Count'}
            fill={CHART_COLORS.primary}
            radius={[0, 4, 4, 0]}
            animationDuration={CHART_CONFIG.animationDuration}
          />
        </BarChart>
      </ChartContainer>
    );
  }

  // Fallback: no chart type matched
  return null;
}

/**
 * Aggregate raw data into chart-ready format based on config.
 */
function aggregateChartData(data, config, range) {
  const groupBy = config.groupBy || 'month';
  const valueField = config.valueField || null;
  const xKey = config.xKey || 'label';
  const yKey = config.yKey || 'value';

  if (groupBy === 'month') {
    return aggregateByMonth(data, valueField, xKey, yKey, range);
  }

  if (groupBy === 'status') {
    return aggregateByField(data, 'status', valueField, xKey, yKey);
  }

  if (groupBy === 'type' || groupBy === 'job_type') {
    const field = config.groupField || 'type';
    return aggregateByField(data, field, valueField, xKey, yKey);
  }

  if (groupBy === 'salesperson') {
    return aggregateByField(data, 'salesperson', valueField, xKey, yKey).slice(0, 10);
  }

  if (groupBy === 'customer') {
    return aggregateByField(data, 'customer_name', valueField, xKey, yKey).slice(0, 10);
  }

  // Default: group by month
  return aggregateByMonth(data, valueField, xKey, yKey, range);
}

/**
 * IDI-777: one point per month from From to To, each counting (or summing)
 * the rows whose report day falls in it. Spans over 12 months label with the
 * year ("Feb 2025"). Without a range, the legacy last-six-months view.
 */
export function aggregateByMonthRange(data, range, { valueField = null, xKey = 'label', yKey = 'value' } = {}) {
  const keys = monthKeysBetween(range.start_date, range.end_date);
  const withYear = keys.length > 12;
  const buckets = new Map(keys.map(k => [k, 0]));
  (data || []).forEach(item => {
    const k = monthKeyOf(item.__reportDay);
    if (!k || !buckets.has(k)) return;
    buckets.set(k, buckets.get(k) + (valueField ? (Number(item[valueField]) || 0) : 1));
  });
  return keys.map(k => ({ [xKey]: monthLabel(k, { withYear }), [yKey]: buckets.get(k) }));
}

function aggregateByMonth(data, valueField, xKey, yKey, range) {
  if (range && range.start_date && range.end_date) {
    return aggregateByMonthRange(data, range, { valueField, xKey, yKey });
  }
  const now = new Date();
  const months = [];

  for (let i = 5; i >= 0; i--) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const label = monthStart.toLocaleDateString('en-US', { month: 'short' });

    const monthItems = data.filter(item => {
      const dateStr = item.created_at || item.createdAt || item.updated_at || item.updatedAt;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= monthStart && d <= monthEnd;
    });

    let value;
    if (valueField) {
      value = monthItems.reduce((sum, item) => sum + (Number(item[valueField]) || 0), 0);
    } else {
      value = monthItems.length;
    }

    months.push({ [xKey]: label, [yKey]: value });
  }

  return months;
}

function aggregateByField(data, field, valueField, xKey, yKey) {
  const groups = {};
  data.forEach(item => {
    const key = item[field] || item[toCamelCase(field)] || item.assigned_to || item.assignedTo || 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  return Object.entries(groups).map(([key, items]) => ({
    [xKey]: humanize(key),
    [yKey]: valueField
      ? items.reduce((sum, item) => sum + (Number(item[valueField]) || 0), 0)
      : items.length,
  }));
}

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function humanize(str) {
  return String(str)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function getFormatter(format) {
  if (format === 'currency') return (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`;
  if (format === 'percent') return (v) => `${v}%`;
  return undefined;
}

function getTooltipFormatter(format) {
  if (format === 'currency') return (v) => formatCurrency(v);
  if (format === 'percent') return (v) => `${v}%`;
  return undefined;
}
